import Dropdown from '@rsa-digital/evo-shared-components/components/Dropdown';
import DateInput, {
  DateValue,
} from '@rsa-digital/evo-shared-components/components/Form/DateInput';
import Field from '@rsa-digital/evo-shared-components/components/Form/Field';
import { Orientation } from '@rsa-digital/evo-shared-components/components/Form/RadioInput/styles';
import TextAreaInput from '@rsa-digital/evo-shared-components/components/Form/TextAreaInput';
import TextInput from '@rsa-digital/evo-shared-components/components/Form/TextInput';
import React from 'react';
import { nonFatalBuildError } from 'helpers/errorReporting';
import {
  trackFormDropdownClick,
  trackFormFieldFocus,
  trackRadioButtonClick,
} from 'helpers/eventTracking';
import { processShowIfField, processTooltip } from 'helpers/questionProcessor';
import { CsQuestion, CsQuestionWithOptions } from 'types/contentStack';
import { StyledRadioInput } from './styles';
import { InputFieldEnum } from '../enums';
import { FieldValueType, FormDataType } from '../types';

type ProcessedFormFieldProps = {
  answerData: FormDataType;
  fieldData: CsQuestion | CsQuestionWithOptions;
  onChange: (fieldKey: string, inputValue: FieldValueType) => void;
  errorMessage?: string;
};

const ProcessedFormField: React.FC<ProcessedFormFieldProps> = ({
  answerData,
  fieldData,
  errorMessage,
  onChange,
}) => {
  const { TEXT, MULTITEXT, DATE, EMAIL, TELEPHONE, POSTCODE, RADIO, SELECT } =
    InputFieldEnum;
  const { type, value: fieldKey, main_details, additional_validators } = fieldData;
  const { question_text, explanatory_text, alert_text } = main_details;
  const fieldOptions = (fieldData as CsQuestionWithOptions).answer_options;
  const showOnConditions = processShowIfField(fieldData);

  const formatDateValueToString = (date: DateValue | undefined): string | undefined =>
    !date || (date && !date.day && !date.month && !date.year)
      ? undefined
      : `${date.day}/${date.month}/${date.year}`;

  const formatStringToDateValue = (date: string | undefined): DateValue => {
    if (!date) {
      return { day: '', month: '', year: '' };
    }
    const splitDate = date.split('/');
    return { day: splitDate[0], month: splitDate[1], year: splitDate[2] };
  };

  const handleDate = (val: DateValue): void => {
    onChange(fieldKey, formatDateValueToString(val));
  };

  const handleTextInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    onChange(fieldKey, e.target.value);
  };

  const handleRadioInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    handleTextInput(e);
    trackRadioButtonClick(question_text, e.target.value);
  };

  const getMaximumLengthByType = (): number | undefined => {
    switch (type) {
      case TELEPHONE:
        return 11;
      case POSTCODE:
        return 8;
      default:
        return undefined;
    }
  };

  const getRenderedField = (): JSX.Element | null => {
    switch (type) {
      case TEXT:
      case EMAIL:
      case TELEPHONE:
      case POSTCODE:
        return (
          <TextInput
            id={fieldKey}
            value={(answerData[`${fieldKey}`] as string) ?? ''}
            onChange={handleTextInput}
            onFocus={trackFormFieldFocus(question_text)}
            numbersOnly={type === TELEPHONE}
            maxLength={getMaximumLengthByType()}
            data-pii-mask
          />
        );
      case MULTITEXT:
        return (
          <TextAreaInput
            id={fieldKey}
            value={(answerData[`${fieldKey}`] as string) ?? ''}
            onChange={handleTextInput}
            onFocus={trackFormFieldFocus(question_text)}
            maxLength={1000}
          />
        );
      case DATE:
        if (
          additional_validators &&
          additional_validators[DATE]?.future_invalid &&
          additional_validators[DATE]?.past_invalid
        ) {
          nonFatalBuildError(
            'Additional Validators for date not properly set.',
            "You can't invalidate the future and the past. You can only choose one or none of the options."
          );
        }
        return (
          <DateInput
            id={fieldKey}
            value={formatStringToDateValue(answerData[`${fieldKey}`])}
            onChange={handleDate}
            onFocus={trackFormFieldFocus(question_text)}
            data-pii-mask
          />
        );
      case RADIO:
        return (
          <StyledRadioInput
            orientationOverride={Orientation.vertical}
            id={fieldKey}
            value={(answerData[`${fieldKey}`] as string) ?? ''}
            options={fieldOptions ?? []}
            onChange={handleRadioInput}
          />
        );
      case SELECT:
        return (
          <Dropdown
            id={fieldKey}
            ariaLabel="This is a dropdown."
            value={(answerData[`${fieldKey}`] as string) ?? ''}
            options={fieldOptions ?? []}
            handleValue={(val: string) => onChange(fieldKey, val)}
            onFocus={trackFormDropdownClick(question_text)}
          />
        );
      default:
        nonFatalBuildError(
          'Input field type not recognized.',
          `'${type}' is not an available input type.`
        );
        return null;
    }
  };

  const renderedField = getRenderedField();
  const showField =
    !showOnConditions ||
    (showOnConditions &&
      showOnConditions.to_contain === answerData[showOnConditions.value]);

  return renderedField !== null && showField ? (
    <Field
      alignLeft
      tooltip={processTooltip(fieldData)}
      label={question_text}
      explanatoryText={explanatory_text}
      alertBody={alert_text}
      errorText={errorMessage}>
      {renderedField}
    </Field>
  ) : (
    <></>
  );
};

export default ProcessedFormField;
